import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { useTopGenres } from '@/hooks/dashboard/useTopGenres'
import { bookListUrl } from '@/lib/utils'
import { ChartContainer } from '../ChartContainer'

// Types for Recharts custom components
interface TooltipPayload {
  value: number
  payload: {
    genre_name: string
    total_books: number
    read_books: number
    percentage: number
    genre_color: string
  }
}

interface CustomTooltipProps {
  active?: boolean
  payload?: TooltipPayload[]
  label?: string
}

export function TopGenresChart() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data, isLoading, error } = useTopGenres(6) // Top 6 genres

  const title = t('dashboard.charts.topGenres')
  const description = t('dashboard.charts.topGenresDescription')

  // Custom tooltip for pie chart
  const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: data.genre_color }} />
            <p className="font-medium text-foreground">{data.genre_name}</p>
          </div>
          <div className="space-y-1 text-sm">
            <p className="text-muted-foreground">
              {t('dashboard.charts.totalBooks')}:{' '}
              <span className="font-medium text-foreground">{data.total_books}</span>
            </p>
            <p className="text-muted-foreground">
              {t('dashboard.charts.booksRead')}:{' '}
              <span className="font-medium text-foreground">{data.read_books}</span>
            </p>
            <p className="text-muted-foreground">
              {t('dashboard.charts.percentageOfLibrary')}:{' '}
              <span className="font-medium text-foreground">{data.percentage}%</span>
            </p>
          </div>
        </div>
      )
    }
    return null
  }

  // Custom label function for pie slices - simplified for TypeScript compatibility
  const renderCustomLabel = () => {
    return ''
  }

  // Show empty state if no data
  if (!isLoading && !error && data.length === 0) {
    return (
      <ChartContainer title={title} description={t('dashboard.charts.noData')}>
        <div className="flex items-center justify-center h-full text-center text-muted-foreground">
          <div>
            <div className="text-4xl mb-4">🎨</div>
            <p className="font-medium mb-1">{t('dashboard.charts.noGenresTitle')}</p>
            <p className="text-sm">{t('dashboard.charts.addGenresToBooks')}</p>
          </div>
        </div>
      </ChartContainer>
    )
  }

  return (
    <ChartContainer
      title={title}
      description={description}
      isLoading={isLoading}
      error={error?.message || null}
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomLabel}
            outerRadius={80}
            fill="#8884d8"
            dataKey="total_books"
            nameKey="genre_name"
          >
            {data.map((entry) => (
              <Cell
                key={entry.genre_id}
                fill={entry.genre_color}
                cursor="pointer"
                onClick={() => navigate(bookListUrl({ status: 'all', genre: entry.genre_id }))}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="bottom"
            height={36}
            wrapperStyle={{
              paddingTop: '10px',
              fontSize: '12px',
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}
